/**
 * Timeline Widget Component - Enhanced Version
 * Collapsible timeline at the bottom of the map showing device activity histogram
 * With cool gradient styling, glow effects, and smooth animations
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Collapse,
  Paper,
  LinearProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  CropFree as CropFreeIcon
} from '@mui/icons-material';
import { copDataService } from '../services/copDataService';

interface TimelineActivityBucket {
  timestamp: string;
  total_count: number;
  student_count: number;
  rp_count: number;
  srp_count: number;
}

// DFP alert shape mirrors modules/PathfinderRF/FE/src/services/dfpAlertsService.ts DFPAlert
interface TLDFPAlert {
  id: number;
  signal_type: string;
  anomaly_type: string;
  severity: string;
  detected_at: string;
  last_seen?: string;
}

interface DFPTimelineBucket {
  hour: string;
  wifi: number;
  cellular: number;
  bluetooth: number;
  gnss: number;
  drone: number;
  critical: number;
  warning: number;
  info: number;
  total: number;
}

const DFP_SIGNAL_TYPE_COLORS: Record<string, string> = {
  wifi: '#00bcd4',
  cellular: '#f44336',
  bluetooth: '#3f51b5',
  gnss: '#ff9800',
  drone: '#9c27b0',
};

const DFP_SEVERITY_COLORS: Record<string, string> = {
  critical: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
};

function buildDFPTimeline(alerts: TLDFPAlert[]): DFPTimelineBucket[] {
  const buckets = new globalThis.Map<string, DFPTimelineBucket>();
  for (const alert of alerts) {
    const ts = alert.detected_at || alert.last_seen;
    if (!ts) continue;
    const d = new Date(ts);
    const hourKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:00`;
    const bucket = buckets.get(hourKey) || { hour: hourKey, wifi: 0, cellular: 0, bluetooth: 0, gnss: 0, drone: 0, critical: 0, warning: 0, info: 0, total: 0 };
    const st = alert.signal_type?.toLowerCase() || '';
    if (st === 'wifi') bucket.wifi++;
    else if (st === 'cellular') bucket.cellular++;
    else if (st === 'bluetooth') bucket.bluetooth++;
    else if (st === 'gnss') bucket.gnss++;
    else if (st.includes('drone')) bucket.drone++;
    const sev = alert.severity?.toLowerCase() || 'info';
    if (sev === 'critical') bucket.critical++;
    else if (sev === 'warning') bucket.warning++;
    else bucket.info++;
    bucket.total++;
    buckets.set(hourKey, bucket);
  }
  return Array.from(buckets.values()).sort((a, b) => a.hour.localeCompare(b.hour));
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface TimelineWidgetProps {
  selectedDate: string | null;
  timeZone?: string;
  selectedCourse: string | null;
  selectedDeviceType: string | null;
  selectedEntityType?: string | null;
  selectedEntity?: string | null;
  /**
   * Preferred: stable tab id from COPDashboardClean's visibleCopTabs
   * ('stats' | 'dwells' | 'geofences' | 'dfp' | ...). Numeric activeTab indices
   * shift under RBAC tab filtering and must not be used for mode selection.
   */
  activeTabId?: string | null;
  /** @deprecated Prefer activeTabId — numeric indices are unstable under RBAC. */
  activeTab?: number;
  mapBounds?: MapBounds | null; // Filter by current map extent
  /**
   * Dashboard grid / embedded panels: normal flow layout and low z-index so parent resize handles work.
   * Default false = docked overlay at bottom of COP map (absolute + high z-index).
   */
  embedded?: boolean;
  /**
   * DFP signal-anomaly alerts. When provided and activeTab is a DFP-related tab,
   * the widget renders a stacked DFP histogram instead of the device-activity bars.
   */
  dfpAlerts?: TLDFPAlert[];
  /**
   * When true, the widget hides Student/RP/SRP-specific stats, legend chips, and
   * tooltip lines since those are gov-specific training taxonomy. Commercial
   * deployments see a neutral "activity per hour" chart instead.
   */
  commercial?: boolean;
}

const TimelineWidget: React.FC<TimelineWidgetProps> = ({
  selectedDate,
  timeZone,
  selectedCourse,
  selectedDeviceType,
  selectedEntityType,
  selectedEntity,
  activeTabId,
  activeTab = 0,
  mapBounds,
  embedded = false,
  dfpAlerts,
  commercial = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Get primary color from theme's color scheme
  const primaryColor = theme.palette.primary.main;
  
  // Convert hex to rgb for rgba usage
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);
  
  // Theme-aware colors - uses theme's color scheme
  const colors = useMemo(() => ({
    background: isDark 
      ? 'linear-gradient(180deg, rgba(30, 32, 40, 0.98) 0%, rgba(24, 26, 32, 0.99) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 250, 0.95) 100%)',
    border: isDark ? `rgba(${primaryRgb}, 0.3)` : `rgba(${primaryRgb}, 0.25)`,
    accent: primaryColor,
    accentGlow: isDark ? `rgba(${primaryRgb}, 0.15)` : `rgba(${primaryRgb}, 0.1)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
    barGradient: isDark 
      ? `linear-gradient(180deg, rgba(${primaryRgb}, 0.9) 0%, rgba(${primaryRgb}, 0.7) 50%, rgba(${primaryRgb}, 0.5) 100%)`
      : `linear-gradient(180deg, rgba(${primaryRgb}, 0.9) 0%, rgba(${primaryRgb}, 0.7) 50%, rgba(${primaryRgb}, 0.5) 100%)`,
    shadow: isDark 
      ? '0 -4px 20px rgba(0, 0, 0, 0.3), 0 -8px 40px rgba(0, 0, 0, 0.4)'
      : '0 -4px 20px rgba(0, 0, 0, 0.08), 0 -8px 40px rgba(0, 0, 0, 0.05)',
    primaryRgb,
  }), [isDark, primaryColor, primaryRgb]);

  const [expanded, setExpanded] = useState(embedded);
  const [timelineData, setTimelineData] = useState<TimelineActivityBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);
  const pendingRefetchRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchTimelineActivityRef = useRef<() => Promise<void>>(async () => {});

  // Listen for PDF capture requests to auto-expand
  useEffect(() => {
    const handleExpandForCapture = () => {
      console.log('[Timeline] Received expand-for-pdf-capture event');
      setExpanded(true);
    };
    
    window.addEventListener('timeline-expand-for-capture', handleExpandForCapture);
    return () => {
      window.removeEventListener('timeline-expand-for-capture', handleExpandForCapture);
    };
  }, []);

  // Resolve mode from stable tab id when provided; fall back to legacy numeric
  // indices only when activeTabId is omitted (embedded/dashboard callers).
  const resolvedTabId = activeTabId ?? (
    activeTab === 5 ? 'dwells'
    : activeTab === 6 ? 'geofences'
    : activeTab === 7 ? 'alerts'
    : activeTab === 9 ? 'dfp'
    : activeTab === 10 ? 'rf_feed'
    : 'stats'
  );

  // DFP mode: when the user is on a DFP-related tab, render the stacked
  // signal-threat histogram instead of fetching the device-activity series.
  const isDFPTab = resolvedTabId === 'dfp' || resolvedTabId === 'rf_feed';
  const isDwellsTab = resolvedTabId === 'dwells';
  const isGeofencesTab = resolvedTabId === 'geofences';
  const skipMapBounds = isDwellsTab || isGeofencesTab || resolvedTabId === 'alerts';

  const dfpTimeline = useMemo(() => {
    if (!dfpAlerts || dfpAlerts.length === 0) return [] as DFPTimelineBucket[];
    return buildDFPTimeline(dfpAlerts);
  }, [dfpAlerts]);

  const dfpStats = useMemo(() => {
    if (dfpTimeline.length === 0) {
      return { max: 0, total: 0, criticalCount: 0, warningCount: 0, peakHour: null as string | null, byType: { wifi: 0, cellular: 0, bluetooth: 0, gnss: 0, drone: 0 } };
    }
    const max = Math.max(...dfpTimeline.map(b => b.total));
    const total = dfpTimeline.reduce((s, b) => s + b.total, 0);
    const criticalCount = dfpTimeline.reduce((s, b) => s + b.critical, 0);
    const warningCount = dfpTimeline.reduce((s, b) => s + b.warning, 0);
    const peak = dfpTimeline.reduce((prev, curr) => (curr.total > prev.total ? curr : prev));
    const byType = {
      wifi: dfpTimeline.reduce((s, b) => s + b.wifi, 0),
      cellular: dfpTimeline.reduce((s, b) => s + b.cellular, 0),
      bluetooth: dfpTimeline.reduce((s, b) => s + b.bluetooth, 0),
      gnss: dfpTimeline.reduce((s, b) => s + b.gnss, 0),
      drone: dfpTimeline.reduce((s, b) => s + b.drone, 0),
    };
    return { max, total, criticalCount, warningCount, peakHour: peak.hour as string | null, byType };
  }, [dfpTimeline]);

  // Get timeline title based on active tab
  const getTimelineTitle = () => {
    switch (resolvedTabId) {
      case 'dwells':
        return 'Dwell Activity Timeline';
      case 'geofences':
        return 'Geofence Activity Timeline';
      case 'alerts':
        return 'Device Activity Timeline'; // Use activity timeline for context around alerts
      case 'dfp':
        return 'Signal Threat Timeline';
      case 'rf_feed':
        return 'RF Signal Feed Timeline';
      default: // Stats tab or others (including Legend which has no specific timeline)
        return 'Activity Timeline';
    }
  };

  // Fetch timeline activity data from backend with optimization
  const fetchTimelineActivity = useCallback(async () => {
    // DFP mode renders client-side from the dfpAlerts prop; skip the fetch.
    if (isDFPTab) return;
    // Prevent concurrent loads, but remember to refetch once the in-flight
    // request finishes (mapBounds often updates while the first call is open).
    if (loadingRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    
    // Cancel any previous pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      let data;
      // For Dwells, Geofences, and Alerts tabs, don't filter by map bounds
      // Users want to see ALL activity for their filters, not just what's visible
      const params = {
        selectedDate,
        timeZone,
        selectedCourse,
        selectedDeviceType,
        selectedEntityType,
        selectedEntity,
        bounds: skipMapBounds ? undefined : (mapBounds || undefined)
      };
      
      // Fetch different data based on active tab
      if (isDwellsTab) {
        data = await copDataService.getTimelineDwells(params);
      } else if (isGeofencesTab) {
        data = await copDataService.getTimelineGeofences(params);
      } else {
        data = await copDataService.getTimelineActivity(params);
      }
      
      setTimelineData(data);
    } catch (error: any) {
      // Ignore abort errors - these are expected when filters change
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('[Timeline] Error fetching activity:', error);
      setError('Failed to load timeline data');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      if (pendingRefetchRef.current) {
        pendingRefetchRef.current = false;
        // Always invoke the latest callback (filters/bounds may have changed
        // while this request was in flight).
        void fetchTimelineActivityRef.current();
      }
    }
  }, [selectedDate, timeZone, selectedCourse, selectedDeviceType, selectedEntityType, selectedEntity, isDFPTab, isDwellsTab, isGeofencesTab, skipMapBounds, mapBounds]);

  fetchTimelineActivityRef.current = fetchTimelineActivity;

  // Debounced fetch when filters change
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    const debounceTimer = setTimeout(() => {
      fetchTimelineActivity();
      
      if (expanded) {
        refreshIntervalRef.current = setInterval(fetchTimelineActivity, 5 * 60 * 1000);
      }
    }, 300);
    
    return () => {
      clearTimeout(debounceTimer);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      // Only cancel this component's request, not all requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedDate, selectedCourse, selectedDeviceType, selectedEntityType, selectedEntity, fetchTimelineActivity, expanded]);
  
  useEffect(() => {
    if (!expanded && refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    } else if (expanded && !refreshIntervalRef.current) {
      refreshIntervalRef.current = setInterval(fetchTimelineActivity, 5 * 60 * 1000);
    }
  }, [expanded, fetchTimelineActivity]);

  // Calculate chart dimensions and max value
  const chartStats = useMemo(() => {
    if (timelineData.length === 0) return { max: 0, totalEvents: 0, peakHour: null, avgPerBucket: 0, dateRange: null };
    
    const max = Math.max(...timelineData.map(b => b.total_count));
    const totalEvents = timelineData.reduce((sum, b) => sum + b.total_count, 0);
    const avgPerBucket = Math.round(totalEvents / timelineData.length);
    
    // Find peak hour
    const peakBucket = timelineData.reduce((prev, curr) => 
      curr.total_count > prev.total_count ? curr : prev
    );
    
    // Calculate date range from first and last bucket
    const firstTimestamp = timelineData[0]?.timestamp;
    const lastTimestamp = timelineData[timelineData.length - 1]?.timestamp;
    let dateRange: string | null = null;
    
    if (firstTimestamp && lastTimestamp) {
      const startDate = new Date(firstTimestamp);
      const endDate = new Date(lastTimestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Format the dates
      const formatDate = (date: Date) => {
        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };
      
      const startStr = formatDate(startDate);
      const endStr = formatDate(endDate);
      
      // If same day, just show that day with time range
      if (startDate.toDateString() === endDate.toDateString()) {
        const startTime = startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        const endTime = endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        dateRange = `${startStr} ${startTime}-${endTime}`;
      } else {
        dateRange = `${startStr} - ${endStr}`;
      }
    }
    
    return { max, totalEvents, peakHour: peakBucket?.timestamp, avgPerBucket, dateRange };
  }, [timelineData]);

  // Format time label (just time, for axis labels)
  const formatTimeLabel = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false
    });
  };

  // Format full date and time label (for tooltips)
  const formatDateTimeLabel = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true
    });
  };

  // const formatDateLabel = (timestamp: string) => {
  //   const date = new Date(timestamp);
  //   const today = new Date();
  //   const yesterday = new Date(today);
  //   yesterday.setDate(yesterday.getDate() - 1);
  //   const dateStr = date.toDateString();
  //   const todayStr = today.toDateString();
  //   const yesterdayStr = yesterday.toDateString();
  //   if (dateStr === todayStr) return 'Today';
  //   if (dateStr === yesterdayStr) return 'Yesterday';
  //   return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
  // };

  // const isNewDay = (currentTimestamp: string, previousTimestamp: string | null) => {
  //    if (!previousTimestamp) return true;
  //    const currentDate = new Date(currentTimestamp).toDateString();
  //    const previousDate = new Date(previousTimestamp).toDateString();
  //    return currentDate !== previousDate;
  // };

  // Format count with k suffix
  const formatCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  // Get gradient color based on value intensity - using theme color
  const getBarGradient = (_heightPercent: number, isHovered: boolean) => {
    const baseOpacity = isHovered ? 1 : 0.85;
    return `linear-gradient(180deg, 
      rgba(${colors.primaryRgb}, ${baseOpacity}) 0%, 
      rgba(${colors.primaryRgb}, ${baseOpacity * 0.8}) 50%,
      rgba(${colors.primaryRgb}, ${baseOpacity * 0.6}) 100%)`;
  };

return (
    <Paper
      id="cop-timeline-widget"
      sx={{
        background: colors.background,
        backdropFilter: 'blur(10px)',
        ...(embedded
          ? {
              position: 'relative',
              height: '100%',
              width: '100%',
              zIndex: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              boxShadow: 'none',
            }
          : {
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              borderTop: `2px solid ${colors.border}`,
              boxShadow: colors.shadow,
            }),
      }}
    >
      {/* Loading Progress Bar - Cyan themed */}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            zIndex: embedded ? 2 : 1001,
            bgcolor: `rgba(${primaryRgb}, 0.1)`,
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${primaryColor}, ${theme.palette.primary.light || primaryColor}, ${primaryColor})`,
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            },
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' },
            }
          }} 
        />
      )}
      
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: `rgba(${primaryRgb}, 0.05)`
          }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: isDark 
              ? `linear-gradient(135deg, rgba(${colors.primaryRgb}, 0.2), rgba(${colors.primaryRgb}, 0.1))`
              : `linear-gradient(135deg, rgba(${colors.primaryRgb}, 0.15), rgba(${colors.primaryRgb}, 0.08))`,
            border: `1px solid ${colors.border}`,
          }}>
            <TimelineIcon sx={{ color: colors.accent, fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ 
            color: colors.text, 
            fontWeight: 'bold',
            textShadow: isDark ? `0 0 10px ${colors.accentGlow}` : 'none'
          }}>
            {getTimelineTitle()}
          </Typography>
          {isDFPTab ? (
            <>
              <Chip
                label={`${dfpStats.total} alerts`}
                size="small"
                sx={{
                  background: dfpStats.criticalCount > 0 ? 'rgba(244,67,54,0.15)' : 'rgba(255,152,0,0.15)',
                  border: `1px solid ${dfpStats.criticalCount > 0 ? 'rgba(244,67,54,0.4)' : 'rgba(255,152,0,0.4)'}`,
                  color: dfpStats.criticalCount > 0 ? '#f44336' : '#ff9800',
                  fontSize: '0.7rem',
                  height: 22,
                  fontWeight: 'bold',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
              {dfpStats.criticalCount > 0 && (
                <Chip label={`${dfpStats.criticalCount} critical`} size="small"
                  sx={{ background: 'rgba(244,67,54,0.15)', border: '1px solid rgba(244,67,54,0.3)', color: '#f44336', fontSize: '0.65rem', height: 22, fontWeight: 'bold', '& .MuiChip-label': { px: 0.5 } }} />
              )}
              {dfpStats.warningCount > 0 && (
                <Chip label={`${dfpStats.warningCount} warning`} size="small"
                  sx={{ background: 'rgba(255,152,0,0.15)', border: '1px solid rgba(255,152,0,0.3)', color: '#ff9800', fontSize: '0.65rem', height: 22, '& .MuiChip-label': { px: 0.5 } }} />
              )}
            </>
          ) : (
          <Chip
            label={`${formatCount(chartStats.totalEvents)} events`}
            size="small"
            sx={{
              background: isDark 
                ? `linear-gradient(135deg, rgba(${colors.primaryRgb}, 0.2), rgba(${colors.primaryRgb}, 0.1))`
                : `linear-gradient(135deg, rgba(${colors.primaryRgb}, 0.15), rgba(${colors.primaryRgb}, 0.08))`,
              border: `1px solid ${colors.border}`,
              color: colors.accent,
              fontSize: '0.7rem',
              height: 22,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1 }
            }}
          />)}
          {!isDFPTab && chartStats.peakHour && (
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
              label={`Peak: ${formatTimeLabel(chartStats.peakHour)}`}
              size="small"
              sx={{
                background: 'rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                color: '#4caf50',
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-label': { px: 0.5 }
              }}
            />
          )}
          {!isDFPTab && chartStats.dateRange && (
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: 12, color: `${colors.textSecondary} !important` }} />}
              label={chartStats.dateRange}
              size="small"
              sx={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: `1px solid ${colors.border}`,
                color: colors.textSecondary,
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-label': { px: 0.5 }
              }}
            />
          )}
          {!isDFPTab && mapBounds && !skipMapBounds && (
            <Chip
              icon={<CropFreeIcon sx={{ fontSize: 12, color: '#2196f3 !important' }} />}
              label="Map View"
              size="small"
              sx={{
                background: 'rgba(33, 150, 243, 0.15)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
                color: '#2196f3',
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-label': { px: 0.5 }
              }}
            />
          )}
        </Box>
        <IconButton 
          size="small" 
          sx={{ 
            color: colors.accent,
            transition: 'transform 0.3s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        >
          <ExpandLessIcon />
        </IconButton>
      </Box>

      {/* Timeline Chart */}
      <Collapse
        in={expanded}
        sx={
          embedded
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiCollapse-wrapper': { flex: 1, minHeight: 0 },
                '& .MuiCollapse-wrapperInner': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
              }
            : undefined
        }
      >
        <Box
          sx={{
            px: embedded ? 1.5 : 3,
            pb: embedded ? 1 : 2,
            pt: embedded ? 0.5 : 1,
            ...(embedded ? { flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' } : {}),
          }}
        >
          {isDFPTab ? (
            dfpTimeline.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>No DFP alert activity</Typography>
              </Box>
            ) : (
              <Box sx={embedded ? { flex: 1, minHeight: 160, display: 'flex', flexDirection: 'column' } : { height: 200 }}>
                {/* DFP Stats Row */}
                <Box sx={{ display: 'flex', gap: embedded ? 1.5 : 3, mb: embedded ? 1 : 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {(Object.entries(dfpStats.byType) as Array<[string, number]>)
                    .filter(([, v]) => v > 0)
                    .map(([type, count]) => (
                      <Box key={type} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: DFP_SIGNAL_TYPE_COLORS[type] || '#999' }}>
                          {count}
                        </Typography>
                        <Typography sx={{ fontSize: '0.55rem', color: '#888', textTransform: 'capitalize' }}>{type}</Typography>
                      </Box>
                    ))}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f44336' }}>{dfpStats.criticalCount}</Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: '#888' }}>Critical</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff9800' }}>{dfpStats.warningCount}</Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: '#888' }}>Warning</Typography>
                  </Box>
                </Box>

                {/* DFP Stacked bars */}
                <Box sx={{
                  display: 'flex',
                  height: embedded ? 120 : 140,
                  alignItems: 'flex-end',
                  gap: '2px',
                  width: '100%',
                  px: 1,
                  flex: embedded ? 1 : undefined,
                  minHeight: embedded ? 80 : undefined,
                }}>
                  {dfpTimeline.map((bucket, index) => {
                    const heightPercent = dfpStats.max > 0 ? (bucket.total / dfpStats.max) * 100 : 0;
                    const isHovered = hoveredIndex === index;
                    const segments = [
                      { key: 'cellular', count: bucket.cellular, color: DFP_SIGNAL_TYPE_COLORS.cellular },
                      { key: 'wifi', count: bucket.wifi, color: DFP_SIGNAL_TYPE_COLORS.wifi },
                      { key: 'bluetooth', count: bucket.bluetooth, color: DFP_SIGNAL_TYPE_COLORS.bluetooth },
                      { key: 'gnss', count: bucket.gnss, color: DFP_SIGNAL_TYPE_COLORS.gnss },
                      { key: 'drone', count: bucket.drone, color: DFP_SIGNAL_TYPE_COLORS.drone },
                    ].filter(s => s.count > 0);

                    return (
                      <Tooltip
                        key={index}
                        title={
                          <Box sx={{ p: 0.5 }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: colors.text }}>
                              {formatDateTimeLabel(bucket.hour)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, mb: 0.5 }}>
                              {bucket.total} alert{bucket.total !== 1 ? 's' : ''}
                            </Typography>
                            {bucket.wifi > 0 && <Typography sx={{ fontSize: '0.65rem', color: DFP_SIGNAL_TYPE_COLORS.wifi }}>WiFi: {bucket.wifi}</Typography>}
                            {bucket.cellular > 0 && <Typography sx={{ fontSize: '0.65rem', color: DFP_SIGNAL_TYPE_COLORS.cellular }}>Cellular: {bucket.cellular}</Typography>}
                            {bucket.bluetooth > 0 && <Typography sx={{ fontSize: '0.65rem', color: DFP_SIGNAL_TYPE_COLORS.bluetooth }}>Bluetooth: {bucket.bluetooth}</Typography>}
                            {bucket.gnss > 0 && <Typography sx={{ fontSize: '0.65rem', color: DFP_SIGNAL_TYPE_COLORS.gnss }}>GNSS: {bucket.gnss}</Typography>}
                            {bucket.drone > 0 && <Typography sx={{ fontSize: '0.65rem', color: DFP_SIGNAL_TYPE_COLORS.drone }}>Drone: {bucket.drone}</Typography>}
                            <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                              {bucket.critical > 0 && <Typography sx={{ fontSize: '0.6rem', color: DFP_SEVERITY_COLORS.critical, fontWeight: 'bold' }}>Critical: {bucket.critical}</Typography>}
                              {bucket.warning > 0 && <Typography sx={{ fontSize: '0.6rem', color: DFP_SEVERITY_COLORS.warning }}>Warning: {bucket.warning}</Typography>}
                              {bucket.info > 0 && <Typography sx={{ fontSize: '0.6rem', color: DFP_SEVERITY_COLORS.info }}>Info: {bucket.info}</Typography>}
                            </Box>
                          </Box>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: { sx: { bgcolor: isDark ? 'rgba(30,32,40,0.95)' : 'rgba(255,255,255,0.95)', border: `1px solid ${colors.border}`, borderRadius: '8px', boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)' } },
                          arrow: { sx: { color: isDark ? 'rgba(30,32,40,0.95)' : 'rgba(255,255,255,0.95)' } },
                        }}
                      >
                        <Box
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', cursor: 'pointer' }}
                        >
                          <Box sx={{
                            width: '100%',
                            height: `${Math.max(heightPercent, bucket.total > 0 ? 4 : 0)}%`,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '3px 3px 0 0',
                            overflow: 'hidden',
                            transition: 'all 0.2s ease',
                            transform: isHovered ? 'scaleY(1.08)' : 'scaleY(1)',
                            transformOrigin: 'bottom',
                            boxShadow: isHovered
                              ? `0 0 12px rgba(244,67,54,0.4), 0 0 24px rgba(244,67,54,0.15)`
                              : bucket.critical > 0
                                ? '0 0 6px rgba(244,67,54,0.3)'
                                : 'none',
                          }}>
                            {segments.map((seg) => (
                              <Box key={seg.key} sx={{ flex: seg.count, bgcolor: seg.color, opacity: isHovered ? 1 : 0.85, minHeight: 2 }} />
                            ))}
                          </Box>
                          {index % Math.max(1, Math.floor(dfpTimeline.length / 8)) === 0 && (
                            <Typography sx={{ color: isHovered ? '#f44336' : '#666', fontSize: '0.55rem', mt: 0.5, whiteSpace: 'nowrap' }}>
                              {formatTimeLabel(bucket.hour)}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* DFP Legend */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap' }}>
                  {Object.entries(DFP_SIGNAL_TYPE_COLORS).filter(([type]) => dfpStats.byType[type as keyof typeof dfpStats.byType] > 0).map(([type, color]) => (
                    <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px', boxShadow: `0 0 4px ${color}40` }} />
                      <Typography sx={{ color: '#888', fontSize: '0.6rem', textTransform: 'capitalize' }}>{type}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, pl: 1, borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: DFP_SEVERITY_COLORS.critical, borderRadius: '50%', boxShadow: `0 0 4px ${DFP_SEVERITY_COLORS.critical}60` }} />
                    <Typography sx={{ color: '#888', fontSize: '0.6rem' }}>Critical</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: DFP_SEVERITY_COLORS.warning, borderRadius: '50%' }} />
                    <Typography sx={{ color: '#888', fontSize: '0.6rem' }}>Warning</Typography>
                  </Box>
                </Box>
              </Box>
            )
          ) : error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
              <Typography variant="body2" sx={{ color: '#f44336' }}>{error}</Typography>
            </Box>
          ) : loading && timelineData.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Loading timeline...</Typography>
            </Box>
          ) : timelineData.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>No activity data available</Typography>
            </Box>
          ) : (
            <Box
              sx={
                embedded
                  ? { flex: 1, minHeight: 160, display: 'flex', flexDirection: 'column' }
                  : { height: 200 }
              }
            >
              {/* Stats Row */}
              <Box sx={{ display: 'flex', gap: embedded ? 1.5 : 3, mb: embedded ? 1 : 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {!commercial && (
                  <>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#4caf50' }}>
                        {timelineData.filter(b => b.student_count > 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>Active Hours (Student)</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ff9800' }}>
                        {timelineData.filter(b => b.rp_count > 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>Active Hours (RP)</Typography>
                    </Box>
                  </>
                )}
                {commercial && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {timelineData.filter(b => b.total_count > 0).length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>Active Hours</Typography>
                  </Box>
                )}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: colors.accent }}>
                    {chartStats.avgPerBucket}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary }}>Avg/Hour</Typography>
                </Box>
              </Box>

              {/* Timeline bars */}
              <Box sx={{ 
                display: 'flex', 
                height: embedded ? 120 : 140, 
                alignItems: 'flex-end', 
                gap: '2px', 
                width: '100%',
                px: 1,
                flex: embedded ? 1 : undefined,
                minHeight: embedded ? 80 : undefined,
              }}>
                {timelineData.map((bucket, index) => {
                  const heightPercent = chartStats.max > 0 ? (bucket.total_count / chartStats.max) * 100 : 0;
                  const isHovered = hoveredIndex === index;
                  const isPeak = chartStats.peakHour === bucket.timestamp;
                  
                  return (
                    <Tooltip
                      key={index}
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: colors.text }}>
                            {formatDateTimeLabel(bucket.timestamp)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                            Total: {bucket.total_count} events
                          </Typography>
                          {!commercial && bucket.student_count > 0 && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#4caf50' }}>
                              Student: {bucket.student_count}
                            </Typography>
                          )}
                          {!commercial && bucket.rp_count > 0 && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#ff9800' }}>
                              RP: {bucket.rp_count}
                            </Typography>
                          )}
                          {!commercial && bucket.srp_count > 0 && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#f44336' }}>
                              SRP: {bucket.srp_count}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: isDark ? 'rgba(30, 32, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            boxShadow: isDark 
                              ? '0 4px 20px rgba(0, 0, 0, 0.4)' 
                              : '0 4px 20px rgba(0, 0, 0, 0.15)',
                            backdropFilter: 'blur(8px)',
                          }
                        },
                        arrow: {
                          sx: {
                            color: isDark ? 'rgba(30, 32, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          }
                        }
                      }}
                    >
                      <Box
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'flex-end',
                          cursor: 'pointer',
                        }}
                      >
                        {/* Bar container */}
                        <Box
                          sx={{
                            width: '100%',
                            height: `${Math.max(heightPercent, bucket.total_count > 0 ? 3 : 0)}%`,
                            background: isPeak 
                              ? 'linear-gradient(180deg, #4caf50 0%, #2e7d32 100%)'
                              : getBarGradient(heightPercent, isHovered),
                            borderRadius: '3px 3px 0 0',
                            transition: 'all 0.2s ease',
                            transform: isHovered ? 'scaleY(1.05)' : 'scaleY(1)',
                            transformOrigin: 'bottom',
                            boxShadow: isHovered 
                              ? `0 0 15px rgba(${colors.primaryRgb}, 0.5), 0 0 30px rgba(${colors.primaryRgb}, 0.2)`
                              : isPeak 
                                ? '0 0 10px rgba(76, 175, 80, 0.4)'
                                : 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            // Shimmer effect on hover
                            '&::after': isHovered ? {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '200%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                              animation: 'barShimmer 0.8s ease',
                            } : {},
                            '@keyframes barShimmer': {
                              '0%': { left: '-100%' },
                              '100%': { left: '100%' },
                            }
                          }}
                        />
                        
                        {/* Time label - show every 4th */}
                        {index % 4 === 0 && (
                          <Typography
                            sx={{
                              color: isHovered ? colors.accent : '#666',
                              fontSize: '0.55rem',
                              mt: 0.5,
                              transition: 'color 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatTimeLabel(bucket.timestamp)}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Legend - activity-only; DFP tabs render their own legend above */}
          {!isDFPTab && timelineData.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              {!commercial && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      background: 'linear-gradient(180deg, #4caf50, #2e7d32)',
                      borderRadius: '2px',
                      boxShadow: '0 0 6px rgba(76, 175, 80, 0.4)'
                    }} />
                    <Typography sx={{ color: '#888', fontSize: '0.65rem' }}>Student</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      background: 'linear-gradient(180deg, #ff9800, #e65100)',
                      borderRadius: '2px',
                      boxShadow: '0 0 6px rgba(255, 152, 0, 0.4)'
                    }} />
                    <Typography sx={{ color: '#888', fontSize: '0.65rem' }}>RP</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      background: 'linear-gradient(180deg, #f44336, #c62828)',
                      borderRadius: '2px',
                      boxShadow: '0 0 6px rgba(244, 67, 54, 0.4)'
                    }} />
                    <Typography sx={{ color: '#888', fontSize: '0.65rem' }}>SRP</Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  background: `linear-gradient(180deg, ${colors.accent}, ${theme.palette.primary.dark || colors.accent})`,
                  borderRadius: '2px',
                  boxShadow: `0 0 6px rgba(${colors.primaryRgb}, 0.4)`
                }} />
                <Typography sx={{ color: '#888', fontSize: '0.65rem' }}>Activity</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TimelineWidget;
